import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  input,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent, TooltipModule } from '@libs/ui-lib';
import { PhoneFormatPipe } from 'libs/patients/src/lib/pipes';
import { PhoneMaskDirective } from 'libs/ui-lib/src/lib/directives';
import { Callback } from '@libs/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ContactData {
  name: string | null;
  phone: string | null;
}

interface ContactFormControls {
  name: FormControl<string | null>;
  phone: FormControl<string | null>;
}

@Component({
  selector: 'lib-health-history-contact-row',
  imports: [CommonModule, TranslateModule, InputComponent, ReactiveFormsModule, TooltipModule, PhoneFormatPipe, PhoneMaskDirective],
  templateUrl: './health-history-contact-row.component.html',
  styleUrl: './health-history-contact-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HealthHistoryContactRowComponent),
      multi: true,
    },
  ],
})
export class HealthHistoryContactRowComponent implements ControlValueAccessor {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  public title = input.required<string>();
  public namePlaceholder = input.required<string>();
  public phonePlaceholder = input.required<string>();
  public isEditMode = input.required<boolean>();
  public validationErrors = input<{ [key: string]: boolean } | null>(null);
  public nameValidationErrorMessage: Signal<string | null> = computed(() => {
    const errors = this.validationErrors();
    if (!errors) return null;
    if (errors['nameRequired']) {
      return 'health_history_patient_secondary_contact_name_error';
    }
    if (errors['nameTooLong']) {
      return 'health_history_patient_secondary_contact_name_too_long_error';
    }
    return null;
  });

  public contactForm = new FormGroup<ContactFormControls>({
    name: new FormControl<string | null>(null),
    phone: new FormControl<string | null>(null),
  });

  private onChange: Callback<ContactData> | null = null;
  private onTouch: Callback<void> | null = null;

  constructor() {
    this.contactForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (this.onChange) {
        this.onChange({
          name: value.name || null,
          phone: value.phone || null,
        });
      }
    });
  }

  public writeValue(value: ContactData | null): void {
    if (value) {
      this.contactForm.patchValue(value, { emitEvent: false });
      this.cdr.markForCheck();
    }
  }

  public registerOnChange(fn: Callback<ContactData>): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: Callback<void>): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.contactForm.disable();
    } else {
      this.contactForm.enable();
    }
  }

  public onBlur(): void {
    if (this.onTouch) {
      this.onTouch();
    }
  }
}
